import * as XLSX from 'xlsx-js-style';

import { NOT_AVAILABLE_UPPERCASE } from '../../shared/AppConstants';
import {
  formatAmountWithCommas,
  getUnitGropAndUnitsList,
  isScopeAnyGlobalType,
  utf8SafeDecode
} from '../../shared/UtilFunctions';

const SEARCH_RESULTS_EXPORT_COLUMNS = [
  'Strategy Number',
  'Strategy Status',
  'Strategy Phase',
  'Owner Name',
  'Approver Name',
  'MGC and MGC Description',
  'Scope',
  'Small Business Form Number',
  'Utilization %',
  'Total Forecast Spend',
  'Strategic Forecast Spend'
];

function sanitizeForExcel(value) {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  return /^[=+\-@]/.test(stringValue) ? `'${stringValue}` : value;
}

function getUserDisplayName(userId, userMap) {
  if (!userId) {
    return NOT_AVAILABLE_UPPERCASE;
  }

  const userInfo = userMap?.get(userId?.toUpperCase());
  const userName = [userInfo?.firstName, userInfo?.lastName]
    .filter(Boolean)
    .join(' ');

  return userName ? utf8SafeDecode(userName) : userId;
}

function getMgcDisplay(strategy) {
  return strategy.decorated?.mgcs?.length
    ? [...strategy.decorated.mgcs].sort().join(', ')
    : NOT_AVAILABLE_UPPERCASE;
}

function getScopeDisplay(strategy) {
  if (isScopeAnyGlobalType(strategy.scope?.level)) {
    return strategy.scope?.level;
  }

  const scopeList = getUnitGropAndUnitsList(strategy.decorated?.scope).sort();

  return scopeList.length
    ? scopeList.join(', ')
    : strategy.scope?.level || NOT_AVAILABLE_UPPERCASE;
}

function getUtilizationDisplay(strategy) {
  return strategy.utilization !== undefined &&
    strategy.utilization !== null &&
    !Number.isNaN(Number(strategy.utilization))
    ? `${Math.round(Number(strategy.utilization).toFixed(3) * 100)}%`
    : NOT_AVAILABLE_UPPERCASE;
}

function getStrategyPhaseDisplay(strategy) {
  return strategy.currentPhase
    ? strategy.currentPhase
    : NOT_AVAILABLE_UPPERCASE;
}

function getSmallBusinessFormDisplay(strategy) {
  return strategy.sdsFormId > 0
    ? strategy.sdsFormId.toString()
    : NOT_AVAILABLE_UPPERCASE;
}

function getSearchResultRows(searchResults, userMap) {
  return searchResults.map((strategy) =>
    [
      strategy.strategyId,
      strategy.status,
      getStrategyPhaseDisplay(strategy),
      getUserDisplayName(strategy.ownerId, userMap),
      getUserDisplayName(strategy.approverId, userMap),
      getMgcDisplay(strategy),
      getScopeDisplay(strategy),
      getSmallBusinessFormDisplay(strategy),
      getUtilizationDisplay(strategy),
      formatAmountWithCommas(
        strategy.forecastTotalSpend,
        true,
        NOT_AVAILABLE_UPPERCASE
      ),
      formatAmountWithCommas(
        strategy.forecastStrategicSpend,
        true,
        NOT_AVAILABLE_UPPERCASE
      )
    ].map(sanitizeForExcel)
  );
}

export function exportSearchResultsToExcel(searchResults, userMap) {
  if (!searchResults || searchResults.length === 0) {
    return;
  }

  const worksheetData = [
    SEARCH_RESULTS_EXPORT_COLUMNS,
    ...getSearchResultRows(searchResults, userMap)
  ];
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  const headerStyle = {
    font: { bold: true, name: 'Verdana', sz: 11, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: '367C2B' },
      bgColor: { rgb: '367C2B' }
    }
  };
  const dataStyle = {
    font: { name: 'Verdana', sz: 10 },
    alignment: { wrapText: true }
  };
  const range = XLSX.utils.decode_range(ws['!ref']);

  for (let column = range.s.c; column <= range.e.c; column++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: column });

    if (ws[cellAddress]) {
      ws[cellAddress].s = headerStyle;
    }
  }

  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    for (let column = range.s.c; column <= range.e.c; column++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: column });

      if (ws[cellAddress]) {
        ws[cellAddress].s = dataStyle;
      }
    }
  }

  ws['!cols'] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 12 },
    { wch: 28 },
    { wch: 28 },
    { wch: 55 },
    { wch: 40 },
    { wch: 26 },
    { wch: 16 },
    { wch: 22 },
    { wch: 24 }
  ];

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, 'Search Results');
  XLSX.writeFile(wb, 'DMSM_Strategy_Search_Results.xlsx');
}
